import React, { useRef } from 'react'
import { ArrowRight, Loader2 } from 'lucide-react'
import { AgentPicker } from '@/frontend/features/Home/AgentPicker'
import { agentHueStyle } from '@/frontend/lib/agentHue'
import type { HomeWorker } from '@/frontend/features/Home/types'
import type { ChatCreateWorker } from './types'

const isReady = (worker: HomeWorker) => worker.chat_ready

function unavailableReason(worker: HomeWorker): string {
  if (worker.provisioning_status === 'ready') return 'Chat ainda não disponível'
  if (worker.provisioning_status === 'failed') return 'Instância indisponível'
  return 'Em preparação…'
}

interface Props {
  workers: ChatCreateWorker[]
  target: ChatCreateWorker
  prompt: string
  sending: boolean
  error: string | null
  onSelect: (id: string) => void
  onPromptChange: (value: string) => void
  onSubmit: () => void
}

export function CreateIntro({
  workers,
  target,
  prompt,
  sending,
  error,
  onSelect,
  onPromptChange,
  onSubmit,
}: Props) {
  const inputRef = useRef<HTMLTextAreaElement>(null)
  const intakeDisabled = !target.intake_enabled
  const canSend = !intakeDisabled && prompt.trim().length > 0 && !sending

  const handleSelect = (id: string) => {
    onSelect(id)
    inputRef.current?.focus()
  }

  const handleKeyDown = (event: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (event.key === 'Enter' && !event.shiftKey) {
      event.preventDefault()
      if (canSend) onSubmit()
    }
  }

  return (
    <main
      style={agentHueStyle(target.id)}
      className="mx-auto flex min-h-[calc(100dvh-4rem)] w-full max-w-3xl flex-1 animate-fade-up overflow-y-auto px-4 sm:px-6 md:min-h-0"
    >
      <div className="my-auto w-full py-10 sm:py-12">
        <section className="max-w-2xl">
          <h1 className="text-[28px] font-semibold tracking-[-0.025em] text-foreground sm:text-[32px]">
            Crie um processo
          </h1>
          <p className="mt-2 max-w-xl text-sm leading-6 text-muted-foreground">
            Descreva o trabalho do seu jeito. O agente organiza as etapas e pede sua confirmação antes de aplicar.
          </p>
        </section>

        <section className="mt-8 w-full rounded-2xl border border-border/80 bg-card p-5 shadow-xs sm:p-6">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <span className="text-xs font-medium text-muted-foreground">Criar com</span>
          <AgentPicker
            workers={workers}
            target={target}
            onSelect={handleSelect}
            align="start"
            isReady={isReady}
            unavailableReason={unavailableReason}
          />
        </div>

        <div className="mt-6">
          <label htmlFor="novo-processo-descricao" className="text-sm font-medium text-foreground">
            Como esse trabalho acontece?
          </label>
          <p className="mt-1 text-xs leading-5 text-muted-foreground">
            Se souber, inclua a ordem das etapas, decisões importantes e o resultado esperado.
          </p>

          {intakeDisabled && (
            <div
              role="status"
              data-testid="novo-processo-intake-desativado"
              className="mt-4 rounded-xl border border-dashed border-border bg-muted/25 px-4 py-4 text-sm leading-6 text-muted-foreground"
            >
              A criação de processos por conversa está desativada para {target.name}. Escolha outro agente
              ou peça a um administrador para habilitar.
            </div>
          )}

          <div
            data-testid="novo-processo-composer"
            className="mt-4 rounded-xl border border-input bg-background transition-[border-color,box-shadow] duration-150 focus-within:border-agent/55 focus-within:ring-[3px] focus-within:ring-brand/10"
          >
            <textarea
              id="novo-processo-descricao"
              ref={inputRef}
              value={prompt}
              onChange={(event) => onPromptChange(event.target.value)}
              onKeyDown={handleKeyDown}
              rows={6}
              autoFocus
              disabled={intakeDisabled || sending}
              data-testid="novo-processo-input"
              aria-label={`Descreva o processo para ${target.name}`}
              placeholder="Descreva quando o trabalho começa, o que precisa acontecer e como ele termina."
              className="max-h-[38dvh] min-h-[clamp(10rem,24dvh,16rem)] w-full resize-none bg-transparent px-4 py-3.5 text-sm leading-6 text-foreground outline-none placeholder:text-muted-foreground/70 disabled:cursor-not-allowed disabled:opacity-50"
            />
            <div className="flex flex-wrap items-center justify-between gap-3 border-t border-border/60 px-3 py-2.5">
              <span className="pl-1 text-[11px] text-faint-foreground">Enter envia · Shift+Enter quebra linha</span>
              <button
                type="button"
                onClick={onSubmit}
                disabled={!canSend}
                data-testid="novo-processo-enviar"
                className="inline-flex h-9 items-center gap-2 rounded-lg bg-primary px-3.5 text-xs font-medium text-primary-foreground transition-[transform,opacity] duration-150 active:scale-[0.98] disabled:cursor-not-allowed disabled:opacity-35"
              >
                {sending ? <Loader2 aria-hidden="true" className="size-3.5 animate-spin" /> : <ArrowRight aria-hidden="true" className="size-3.5" />}
                {sending ? 'Iniciando…' : 'Estruturar processo'}
              </button>
            </div>
          </div>

          {error && (
            <p role="alert" className="mt-3 text-[12.5px] text-destructive">
              {error}
            </p>
          )}
        </div>
        </section>
      </div>
    </main>
  )
}
